import React, { useEffect, useState, useMemo, useCallback, useContext } from "react";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
} from "@mui/material";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import Checkbox from "components/Input/Checkbox/Checkbox";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { getCardTypeIcon } from "utils/utils";
import {
  updateMerchantMdr,
  updateGsi,
  createMerchantMdr,
  createGsi,
  queryAmexSponsoredMerchant,
} from "../model";
import AmexSponsoredMerchantDialog from "../Component/amex/AmexSponsoredMerchantDialog";
import { filterDifferences } from "../utils";
import styles from "./MerchantProcessorMDR.module.scss";
import { PortalContext } from "App";
import useActionSnackBar from "utils/useActionSnackBar/useActionSnackBar";
const PaymentGateways = ({
  merchantId,
  merchantMdrList,
  mccOptions,
  fetchMerchantInfoAndGsi,
  merchant,
  path,
}) => {
  const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.processorMdr.";
  const MERCHANT_PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";
  const { t } = useTranslation();
  const stateContext = useContext(PortalContext);
  const { setDetailPageState } = stateContext;

  const [mdrList, setMdrList] = useState(merchantMdrList ?? []);
  const [amexProcessorEnabled, setAmexProcessorEnabled] = useState(undefined);
  const [isEditing, setIsEditing] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  /* --- AMEX ---*/
  const [isAmexBbmslFormDiaglogOpen, setIsAmexBbmslFormDiaglogOpen] =
    useState(false);
  const [amexSponsoredMerchant, setAmexSponsoredMerchant] = useState(null);
  const [hasFetchedAmexData, setHasFetchedAmexData] = useState(false);
  const [isAmexFormLoading, setIsAmexFormLoading] = useState(false);
  const [forceAmexEnabled, setForceAmexEnabled] = useState(false);

  const { actionSnackBar } = useActionSnackBar(PAGE_LOCALE_KEY);

  const businessPlanMapper = {
    sme: t(`${PAGE_LOCALE_KEY}gsi.businessPlanOptions.sme`),
    normal: t(
      `${PAGE_LOCALE_KEY}gsi.businessPlanOptions.standard`,
    ),
  };

  const rules = {
    mdr: (value) => validateMdr(value),
    foreignMdr: (value) => validateMdr(value),
    referrerCommission: (value) => validateMdr(value),
  };

  const validateMdr = (value) => {
    if (value === "") return true;
    if (!/^\d*\.?\d{0,2}$/.test(value) || value === ".") return false;
    const num = parseFloat(value);
    return num >= 0 && num <= 100;
  };

  const handleMerchantMdrOnChangeWithValidation = (
    merchantMdr,
    input,
    field,
  ) => {
    const isValid = rules[field](input);
    if (!isValid) {
      return;
    }
    updateMerchantMdrState(merchantMdr?.cardType, field, input);
  };

  useEffect(() => {
    setMdrList(merchantMdrList);

    if (!merchantMdrList?.length) return;

    const amexProcessorEnabled = merchantMdrList
      .find((mdr) => mdr.cardType === "AMEX")
      ?.gatewaySwitchingInfoList?.find((gsi) => gsi?.enabled)?.paymentGateway;

    setAmexProcessorEnabled(amexProcessorEnabled);

    // Persist AMEX_BBMSL enabled in UI even if backend data hasn't reflected yet
    if (forceAmexEnabled) {
      updateGsiState("AMEX", "AMEX_BBMSL", "enabled", true);
    }
  }, [merchantMdrList, forceAmexEnabled]);

  // Sort data by priority order
  const sortedData = useMemo(() => {
    if (!Array.isArray(mdrList)) {
      return [];
    }

    const cardTypeOrder = [
      "VISA",
      "MASTER",
      "UNIONPAY",
      "JCB",
      "AMEX",
      "OCTOPUS",
      "WECHAT",
      "ALIPAY",
      "PAYME",
    ];
    const priorityMap = Object.fromEntries(
      cardTypeOrder.map((type, index) => [type, index]),
    );

    return [...mdrList].sort((a, b) => {
      const orderA = priorityMap[a.cardType] ?? Infinity;
      const orderB = priorityMap[b.cardType] ?? Infinity;
      return orderA - orderB;
    });
  }, [mdrList]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString).format("YYYY-MM-DD HH:mm:ss");
  };
  // --- Handle Edit Status For merchant MDR & Gsi -- //
  const setIsEditingState = (cardType, processor, boolean) => {
    if (!cardType) return;

    const update = {
      [cardType]: processor ? { [processor]: boolean } : { mdr: boolean },
    };

    setIsEditing(update);
  };

  // --- Update single input in merchant mdr row ---/
  const updateMerchantMdrState = (cardType, field, inputValue) => {
    setMdrList((prevState) =>
      prevState.map((merchantMdr) =>
        merchantMdr.cardType === cardType
          ? { ...merchantMdr, [field]: inputValue }
          : merchantMdr,
      ),
    );
  };

  // --- Update single input in gsi row ---/
  const updateGsiState = (cardType, processor, field, inputValue) => {
    setMdrList((prevState) =>
      prevState.map((merchantMdr) => {
        if (merchantMdr.cardType !== cardType) return merchantMdr;
        return {
          ...merchantMdr,
          gatewaySwitchingInfoList: merchantMdr.gatewaySwitchingInfoList.map(
            (gsi) => {
              // Handle "inUse" updates
              if (field === "inUse") {
                // Case 1: "NONE" - disable all
                if (processor === "NONE") {
                  return { ...gsi, inUse: false };
                }
                // Case 2: Specific processor - enable only matching one
                return {
                  ...gsi,
                  inUse: gsi.paymentGateway === processor ? inputValue : false,
                };
              }

              // Handle other fields
              if (gsi.paymentGateway === processor) {
                return { ...gsi, [field]: inputValue };
              }

              return gsi;
            },
          ),
        };
      }),
    );
  };

  const getMerchantMdrFromState = (cardType) => {
    return mdrList.find((merchantMdr) => merchantMdr.cardType === cardType);
  };

  const getMerchantMdrFromCache = (cardType) => {
    return merchantMdrList.find(
      (merchantMdr) => merchantMdr.cardType === cardType,
    );
  };

  const getGsiFromState = (cardType, processor) => {
    return getMerchantMdrFromState(cardType)?.gatewaySwitchingInfoList.find(
      (gsi) => gsi.paymentGateway === processor,
    );
  };

  const getGsiFromCache = (cardType, processor) => {
    return getMerchantMdrFromCache(cardType)?.gatewaySwitchingInfoList.find(
      (gsi) => gsi.paymentGateway === processor,
    );
  };

  const resetState = () => {
    setMdrList(merchantMdrList);
  };

  const handleMerchantMdrOperation = async (merchantMdr) => {
    setIsEditingState(merchantMdr?.cardType, null, true);
    setIsLoading(true);
    const previousMerchantMdr = getMerchantMdrFromCache(merchantMdr?.cardType);
    const newMerchantMdr = {
      ...merchantMdr,
      paymentGatewayInUse: getInUseProcessorFromGsiList(
        merchantMdr?.gatewaySwitchingInfoList,
      ),
    };
    const payload = filterDifferences(
      previousMerchantMdr,
      newMerchantMdr,
      null,
      ["gatewaySwitchingInfoList"],
    );
    // Early exit if no changes detected
    if (!payload || Object.keys(payload).length === 0) {
      actionSnackBar("noChanges", "info", null);
      setIsEditingState(merchantMdr?.cardType, null, false);
      setIsLoading(false);
      return;
    }
    try {
      const response =
        previousMerchantMdr?.id && previousMerchantMdr?.id !== ""
          ? await updateMerchantMdr(merchantMdr?.id, {
            ...payload,
            merchantId,
          })
          : await createMerchantMdr({
              ...payload,
              merchantId,
              cardType: merchantMdr?.cardType,
            });
      const { data } = response;
      if (data?.responseCode === "0000") {
        actionSnackBar("save", "success", null);
      } else {
        throw new Error(data?.message);
      }
    } catch (error) {
      actionSnackBar("save", "failed", {errorMessage: error?.message ?? "unknown error"});
    } finally {
      await fetchMerchantInfoAndGsi("update");
      setIsEditingState(merchantMdr?.cardType, null, false);
      setIsLoading(false);
    }
  };

  const handleGsiOperation = async (gsi) => {
    setIsEditingState(gsi?.cardType, gsi?.paymentGateway, true);
    setIsLoading(true);
    const previousGsi = getGsiFromCache(gsi?.cardType, gsi?.paymentGateway);
    const payload = filterDifferences(previousGsi, gsi);
    // Early exit if no changes detected
    if (!payload || Object.keys(payload).length === 0) {
      actionSnackBar("noChanges", "info", null);
      setIsEditingState(gsi?.cardType, gsi?.paymentGateway, false);
      setIsLoading(false);
      return;
    }
    const action =
      previousGsi?.id && previousGsi?.id !== "" ? "update" : "create";
    try {
      // check gsi's id
      // case 1: gsi has gsi'id -> update
      // case 2: gsi no gsi id -> create
      // create pending create gsi finish
      const response =
        action === "update"
          ? await updateGsi(gsi?.id, payload)
          : await createGsi({
              ...payload,
              merchantId,
              cardType: gsi?.cardType,
              paymentGateway: gsi?.paymentGateway,
            });
      const { data } = response;
      if (data?.responseCode === "0000") {
        actionSnackBar("save", "success", null);
        // If enabled was turned on from false -> true, notify Processor Accounts to refresh
        if (
          (!previousGsi?.enabled && gsi?.enabled) ||
          (gsi?.paymentGateway === "AMEX_BBMSL" &&
            previousGsi?.businessPlan !== gsi?.businessPlan)
        ) {
          const timestamp = Date.now();
          setDetailPageState((prev) => ({
            ...prev,
            [path]: {
              ...prev[path],
              mdrRefreshTrigger: timestamp,
            },
          }));
        }
      } else {
        throw new Error(data?.message);
      }
    } catch (error) {
      actionSnackBar("save", "failed", {errorMessage: error?.message ?? "unknown error"});
    } finally {
      await fetchMerchantInfoAndGsi("update");
      // setIsGsiEditingState(gsi?.cardType, gsi?.paymentGateway, false);
      setIsEditingState(gsi?.cardType, gsi?.paymentGateway, false);
      setIsLoading(false);
    }
  };

  const findMccObject = (mccCode) => {
    const result = mccOptions?.find((item) => item.value === mccCode);
    return result;
  };

  const getInUseProcessorFromGsiList = (gsiList) => {
    const inUseGateway =
      gsiList && gsiList.length > 0
        ? gsiList.find((gsi) => gsi.inUse === true || gsi.inUse === 1)
        : "NONE";
    return inUseGateway?.paymentGateway ?? "NONE";
  };

  const businessPlanOptions = useCallback((gsi) => {
    const businessPlanOptions = ["sme", "normal"].map((plan) => ({
      title: businessPlanMapper[plan],
      value: plan,
      label: businessPlanMapper[plan],
    }));

    // only allow sme for first onboard on AMEX_BBMSL
    if (gsi && gsi.paymentGateway === "AMEX_BBMSL" && !gsi.id) {
      return [
        {
          title: businessPlanMapper["sme"],
          value: "sme",
          label: businessPlanMapper["sme"],
        },
      ];
    }

    return businessPlanOptions;
  }, []);

  const specialHandlingConfig = {
    merchantMdr: {
      mdr: {
        notAllowInputProcessors: ["OCTOPUS", "AMEX"],
      },
      foreignMdr: {
        inputDimmedProcessors: ["UNIONPAY", "JCB"],
        notAllowInputCardTypes: ["OCTOPUS", "ALIPAY", "PAYME", "WECHAT"],
        notAllowInputProcessors: ["AMEX"],
      },
    },
    gsi: {
      businessPlan: {
        notAllowInputProcessors: [
          "OCTOPUS",
          "OCTOPUS_BBMSL",
          "OCTOPUS_HKBN",
          "ALIPAY",
          "HSBC",
          "AMEX",
          "WECHAT_PAY",
        ],
      },
      mcc: {
        notAllowInputProcessors: [
          "OCTOPUS",
          "OCTOPUS_BBMSL",
          "OCTOPUS_HKBN",
          "AMEX",
        ],
      },
      mid: {
        allowInputProcessors: [
          "OCTOPUS_BBMSL",
          "OCTOPUS_HKBN",
          "AMEX",
          "WECHAT_PAY",
          "ALIPAY",
        ],
      },
    },
  };

  // Helper predicates based on specialHandlingConfig
  const isBusinessPlanAllowed = (processor) =>
    !specialHandlingConfig.gsi.businessPlan.notAllowInputProcessors.includes(
      processor,
    );

  const isMccAllowed = (processor) =>
    !specialHandlingConfig.gsi.mcc.notAllowInputProcessors.includes(processor);

  const isMdrAllowed = (processor) =>
    !specialHandlingConfig.merchantMdr.mdr.notAllowInputProcessors.includes(
      processor,
    );

  const isForeignMdrAllowedForCardType = (cardType) =>
    !specialHandlingConfig.merchantMdr.foreignMdr.notAllowInputCardTypes.includes(
      cardType,
    );

  const isForeignMdrAllowedForProcessor = (processor) =>
    !specialHandlingConfig.merchantMdr.foreignMdr.notAllowInputProcessors.includes(
      processor,
    );

  const renderForeignMdr = useCallback(
    (isEditing, merchantMdr) => {
      const inUseProcessor = getInUseProcessorFromGsiList(
        merchantMdr?.gatewaySwitchingInfoList,
      );
      const allowed =
        isForeignMdrAllowedForCardType(merchantMdr?.cardType) &&
        isForeignMdrAllowedForProcessor(inUseProcessor);
      if (isEditing && allowed) {
        return (
          <OutlinedText
            percentageSymbol={true}
            // The CSS class like styles.xx is used for className, but divStyle is required for inline styles like {width: "150px"}
            divStyle={{ width: "150px" }}
            noErrorMessage
            disabled={handleDisable(merchantMdr?.cardType, null, "foreignMdr")}
            value={merchantMdr?.foreignMdr}
            onchangeFn={(e) => {
              handleMerchantMdrOnChangeWithValidation(
                merchantMdr,
                e.target.value,
                "foreignMdr",
              );
            }}
          />
        );
      }
      if (!allowed) return `-`;
      const foreignMdrValue = merchantMdr?.foreignMdr;
      const shouldShowDash = !foreignMdrValue || foreignMdrValue === "0" || foreignMdrValue === "0.00" || parseFloat(foreignMdrValue) === 0;
      return shouldShowDash ? `-` : `${merchantMdr.foreignMdr} %`;
    },
    [updateGsiState],
  );

  const renderMdr = useCallback(
    (isEditing, merchantMdr) => {
      const inUseProcessor = getInUseProcessorFromGsiList(
        merchantMdr?.gatewaySwitchingInfoList,
      );
      if (!isMdrAllowed(inUseProcessor)) {
        return "-";
      }
      if (isEditing) {
        return (
          <OutlinedText
            percentageSymbol={true}
            divStyle={{ width: "150px" }}
            noErrorMessage
            disabled={handleDisable(merchantMdr?.cardType, null, "mdr")}
            id={`${merchantMdr?.cardType}-mdr`}
            value={validateMdr(merchantMdr?.mdr) ? merchantMdr?.mdr : ""}
            onchangeFn={(e) => {
              handleMerchantMdrOnChangeWithValidation(
                merchantMdr,
                e.target.value,
                "mdr",
              );
            }}
          />
        );
      }
      const mdrValue = merchantMdr?.mdr;
      const shouldShowDash = !mdrValue || mdrValue === "0" || mdrValue === "0.00" || parseFloat(mdrValue) === 0;
      return shouldShowDash ? `-` : `${merchantMdr.mdr} %`;
    },
    [updateGsiState],
  );

  const renderBusinessPlan = useCallback(
    (isEditing, merchantMdr, gsi) => {
      const { cardType } = merchantMdr || {};
      const { paymentGateway, businessPlan } = gsi || {};
      if (!isBusinessPlanAllowed(paymentGateway)) {
        return "-";
      }
      if (isEditing) {
        return (
          <Selection
            noErrorMessage
            disabled={isLoading}
            selectionValue={businessPlan}
            options={businessPlanOptions(gsi) ?? []}
            onchangeFn={(e) =>
              updateGsiState(
                cardType,
                gsi?.paymentGateway,
                "businessPlan",
                e.target.value,
              )
            }
          />
        );
      }
      return businessPlanMapper[businessPlan] ?? "-";
    },
    [isLoading, businessPlanOptions, updateGsiState],
  );

  const handleDisable = useCallback(
    (cardType, processor, field) => {
      if (
        cardType === "AMEX" &&
        amexProcessorEnabled &&
        ((field && field !== "inUse" && amexProcessorEnabled === "AMEX") ||
          (processor && amexProcessorEnabled !== processor))
      ) {
        return true;
      }

      if (field === "foreignMdr") {
        return (
          isLoading ||
          specialHandlingConfig.merchantMdr.foreignMdr.inputDimmedProcessors.includes(
            cardType,
          )
        );
      }

      return isLoading;
    },
    [isLoading, amexProcessorEnabled],
  );

  // Function to handle GSI enabled checkbox change
  const handleGsiEnabledChange = async (e, merchantMdr, gsi) => {
    const isChecked = e.target.checked;

    // Update GSI state
    updateGsiState(
      merchantMdr?.cardType,
      gsi?.paymentGateway,
      "enabled",
      isChecked,
    );
    // Handle AMEX_BBMSL specific logic
    if (gsi?.paymentGateway === "AMEX_BBMSL" && isChecked) {
      await handleAmexEnabledClick(isChecked, merchantMdr, gsi);
    }
  };

  const handleAmexEnabledClick = async (isChecked, merchantMdr, gsi) => {
    if (!isChecked || gsi?.paymentGateway !== "AMEX_BBMSL") return;
    // If already fetched in this session
    if (hasFetchedAmexData) {
      // If data exists → nothing to do
      if (amexSponsoredMerchant) return;
      // Not registered → open dialog with form, no refetch
      setIsAmexFormLoading(false);
      setIsAmexBbmslFormDiaglogOpen(true);
      return;
    }

    // Show dialog immediately with loading content
    setIsAmexBbmslFormDiaglogOpen(true);

    // Fetch before deciding whether to keep the dialog open
    try {
      setIsAmexFormLoading(true);
      const resp = await queryAmexSponsoredMerchant({ merchantId });
      if (resp?.data?.responseCode === "0000" && resp?.data?.data) {
        // found: cache and allow dialog to auto-close from loading state
        setAmexSponsoredMerchant(resp.data.data);
        // ensure the toggle remains enabled in state
        updateGsiState(
          merchantMdr?.cardType,
          gsi?.paymentGateway,
          "enabled",
          true,
        );
        // close dialog shortly to avoid flash
        setIsAmexBbmslFormDiaglogOpen(false);
      } else {
        // not found: ensure no cached data so dialog will render the form
        setAmexSponsoredMerchant(null);
      }
    } catch (e) {
      setAmexSponsoredMerchant(null);
    } finally {
      setHasFetchedAmexData(true);
      setIsAmexFormLoading(false);
    }
  };

  const renderSaveButtons = (merchantMdr, gsi) => {
    const isRowEditing =
      isEditing?.[merchantMdr?.cardType]?.[gsi?.paymentGateway];

    // Only render Save button when editing
    if (!isRowEditing) {
      return null;
    }

    const commonProps = {
      variant: "normal-button",
      disabled: isLoading,
      loading: isLoading,
      sx: {
        width: "80px",
        display: "flex",
        visibility: "visible",
      },
    };

    return (
      <LoadingButton
        {...commonProps}
        onClick={async () => {
          await handleGsiOperation(
            getGsiFromState(merchantMdr?.cardType, gsi?.paymentGateway),
          );
        }}
      >
        {t(`${MERCHANT_PAGE_LOCALE_KEY}button.save`)}
      </LoadingButton>
    );
  };

  return (
    <div>
      {sortedData?.map((merchantMdr, index) => {
        if (merchantMdr.specialProcessor) {
          return null;
        }
        const cachedMerchantMdr = merchantMdrList?.find(
          (cachedMdr) => merchantMdr.cardType === cachedMdr.cardType,
        );
        const inUseGsi = cachedMerchantMdr?.gatewaySwitchingInfoList?.find(
          (gsi) => gsi?.inUse === 1 || gsi?.inUse === true,
        );
        const inUseOptions = cachedMerchantMdr?.gatewaySwitchingInfoList
          ?.map((gsi) => {
            if (gsi?.enabled === 1 || gsi?.enabled === true) {
              return {
                title: gsi.paymentGateway,
                value: gsi.paymentGateway,
                label: gsi.paymentGateway,
              };
            }
          })
          .filter(Boolean);
        inUseOptions?.push({
          title: "NONE",
          value: "NONE",
          label: "NONE",
        });

        return (
          <>
            <TableContainer
              key={merchantMdr.id}
              component={Paper}
              className={styles.tableContainer}
              sx={{
                mb: index < sortedData.length - 1 ? 4 : 0,
              }}
            >
              <Table aria-label={`combined-table-${merchantMdr.id}`}>
                {/* Card Type Details Header */}
                <TableHead className={styles.merchantMdrHeader}>
                  <TableRow>
                    <TableCell className={styles.tableCell}>
                      {t(`${PAGE_LOCALE_KEY}merchantMdr.cardType`)}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {t(`${PAGE_LOCALE_KEY}merchantMdr.inUse`)}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {t(`${PAGE_LOCALE_KEY}merchantMdr.mdr`)}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {t(
                        `${PAGE_LOCALE_KEY}merchantMdr.foreignMdr`,
                      )}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {t(
                        `${PAGE_LOCALE_KEY}merchantMdr.referrerCommission`,
                      )}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {t(`${PAGE_LOCALE_KEY}merchantMdr.remark`)}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {t(
                        `${PAGE_LOCALE_KEY}merchantMdr.inUseEffectiveDate`,
                      )}
                    </TableCell>
                    <TableCell className={styles.actionCell}>
                      {t(`${PAGE_LOCALE_KEY}merchantMdr.action`)}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Card Type Details Row */}
                <TableBody>
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      className={styles.merchantMdrRow}
                    >
                      <img
                        src={getCardTypeIcon(merchantMdr.cardType)}
                        height={36}
                      />
                    </TableCell>
                    <TableCell>
                      {isEditing[merchantMdr?.cardType]?.mdr ? (
                        <Selection
                          divStyle={{ width: "150px" }}
                          noErrorMessage
                          disabled={handleDisable(
                            merchantMdr?.cardType,
                            null,
                            "inUse",
                          )}
                          options={inUseOptions ?? []}
                          defaultValue={inUseGsi?.paymentGateway || "NONE"}
                          onchangeFn={(e) => {
                            updateGsiState(
                              merchantMdr?.cardType,
                              e.target.value,
                              "inUse",
                              true,
                            );
                            if (e.target.value === "OCTOPUS") {
                              updateMerchantMdrState(
                                merchantMdr?.cardType,
                                "mdr",
                                0,
                              );
                            }
                          }}
                        />
                      ) : (
                        inUseGsi?.paymentGateway || "NONE"
                      )}
                    </TableCell>
                    <TableCell>
                      {renderMdr(
                        isEditing[merchantMdr?.cardType]?.mdr,
                        merchantMdr,
                      )}
                    </TableCell>
                    <TableCell>
                      {renderForeignMdr(
                        isEditing[merchantMdr?.cardType]?.mdr,
                        merchantMdr,
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing[merchantMdr?.cardType]?.mdr ? (
                        <OutlinedText
                          percentageSymbol={true}
                          divStyle={{ width: "150px" }}
                          noErrorMessage
                          disabled={handleDisable(
                            merchantMdr?.cardType,
                            null,
                            "referrerCommission",
                          )}
                          id={`${merchantMdr?.cardType}-referrerCommission`}
                          value={validateMdr(merchantMdr?.referrerCommission) ? merchantMdr?.referrerCommission : ""}
                          onchangeFn={(e) => {
                            handleMerchantMdrOnChangeWithValidation(
                              merchantMdr,
                              e.target.value,
                              "referrerCommission",
                            );
                          }}
                        />
                      ) : (
                        (() => {
                          const referrerCommissionValue = merchantMdr?.referrerCommission;
                          const shouldShowDash = !referrerCommissionValue || referrerCommissionValue === "0" || referrerCommissionValue === "0.00" || parseFloat(referrerCommissionValue) === 0;
                          return shouldShowDash ? `-` : `${merchantMdr.referrerCommission} %`;
                        })()
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing[merchantMdr?.cardType]?.mdr ? (
                        <OutlinedText
                          divStyle={{
                            width: "150px",
                            wordWrap: "break-word",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                          }}
                          noErrorMessage
                          disabled={handleDisable(
                            merchantMdr?.cardType,
                            null,
                            "remark",
                          )}
                          defaultValue={merchantMdr.remark}
                          onchangeFn={(e) => {
                            updateMerchantMdrState(
                              merchantMdr?.cardType,
                              "remark",
                              e.target.value,
                            );
                          }}
                        />
                      ) : (
                        <Stack
                          sx={{
                            maxWidth: "150px",
                            minWidth: "150px",
                          }}
                        >
                          {merchantMdr.remark || "-"}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(merchantMdr.inUseEffectiveDate)}
                    </TableCell>
                    <TableCell className={styles.actionCell}>
                      <Stack
                        sx={{
                          color: "#006A8E",
                          cursor: handleDisable(merchantMdr?.cardType, null)
                            ? "not-allowed"
                            : "pointer",
                          pointerEvents: handleDisable(
                            merchantMdr?.cardType,
                            null,
                          )
                            ? "none"
                            : "auto",
                          opacity: handleDisable(merchantMdr?.cardType, null)
                            ? 0.6
                            : 1,
                          display: isEditing[merchantMdr?.cardType]?.mdr
                            ? "none"
                            : "block",
                        }}
                        onClick={() => {
                          resetState();
                          setIsEditingState(merchantMdr?.cardType, null, true);
                        }}
                      >
                        <div>{t(`${MERCHANT_PAGE_LOCALE_KEY}button.edit`)}</div>
                      </Stack>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"flex-start"}
                        gap={2}
                        flexWrap={"wrap"}
                      >
                        <LoadingButton
                          variant="normal-button"
                          loading={isLoading}
                          sx={{
                            width: "80px",
                            display: !isEditing[merchantMdr?.cardType]?.mdr
                              ? "none"
                              : "flex",
                            visibility: !isEditing[merchantMdr?.cardType]?.mdr
                              ? "hidden"
                              : "",
                          }}
                          onClick={async () => {
                            await handleMerchantMdrOperation(
                              getMerchantMdrFromState(merchantMdr?.cardType),
                            );
                          }}
                        >
                          {t(`${MERCHANT_PAGE_LOCALE_KEY}button.save`)}
                        </LoadingButton>
                        <Button
                          sx={{
                            display: !isEditing[merchantMdr?.cardType]?.mdr
                              ? "none"
                              : "block",
                            visibility: !isEditing[merchantMdr?.cardType]?.mdr
                              ? "hidden"
                              : "",
                          }}
                          variant="outline-normal-button"
                          onClick={() => {
                            setIsEditingState(
                              merchantMdr?.cardType,
                              null,
                              false,
                            );
                            resetState();
                          }}
                          type="button"
                          disabled={isLoading}
                        >
                          {t(`${MERCHANT_PAGE_LOCALE_KEY}button.cancel`)}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableBody>

                {/* --------------------------------------------- GSI Session ------------------------------------------- */}
                {/* Gsi' Header */}
                <TableHead className={styles.gsiHeader}>
                  <TableRow>
                    <TableCell className={styles.gsiTableCell}>
                      {t(`${PAGE_LOCALE_KEY}gsi.paymentGateway`)}
                    </TableCell>
                    <TableCell className={styles.gsiTableCell}>
                      {t(`${PAGE_LOCALE_KEY}gsi.enabled`)}
                    </TableCell>
                    <TableCell className={styles.gsiTableCell} colSpan={3}>
                      {t(`${PAGE_LOCALE_KEY}gsi.mcc`)}
                    </TableCell>
                    <TableCell className={styles.gsiTableCell}>
                      {t(`${PAGE_LOCALE_KEY}gsi.businessPlan`)}
                    </TableCell>
                    <TableCell className={styles.gsiTableCell}>
                      {t(`${PAGE_LOCALE_KEY}gsi.updated`)}
                    </TableCell>
                    <TableCell className={styles.gsiActionCell}>
                      {t(`${PAGE_LOCALE_KEY}gsi.action`)}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Gsi Rows */}
                <TableBody>
                  {merchantMdr?.gatewaySwitchingInfoList?.map((gsi) => (
                    <TableRow key={gsi?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className={styles.gsiRow}
                      >
                        {gsi?.paymentGateway}
                      </TableCell>
                      <TableCell className={styles.gsiTableCell} >
                        <Checkbox
                          id={`checkbox-${gsi?.id}`}
                          checked={gsi?.enabled === 1 || gsi?.enabled === true}
                          disabled={
                            !isEditing?.[merchantMdr?.cardType]?.[
                              gsi?.paymentGateway
                            ] || isLoading
                          }
                          oncheckFn={(e) =>
                            handleGsiEnabledChange(e, merchantMdr, gsi)
                          }
                          inputProps={{ "aria-label": `checkbox-${gsi.id}` }}
                        />
                      </TableCell>
                      <TableCell className={styles.gsiTableCell} colSpan={3}>
                        {(() => {
                          const isRowEditing =
                            isEditing?.[merchantMdr?.cardType]?.[
                              gsi?.paymentGateway
                            ];
                          const mccAllowed = isMccAllowed(gsi?.paymentGateway);
                          if (isRowEditing && mccAllowed) {
                            return (
                              <Selection
                                divStyle={{ width: "inherit" }}
                                noErrorMessage
                                disabled={isLoading}
                                id={`${gsi?.paymentGateway}-mcc`}
                                defaultValue={gsi?.mcc}
                                selectionValue={gsi?.mcc}
                                onchangeFn={(e) => {
                                  updateGsiState(
                                    merchantMdr?.cardType,
                                    gsi?.paymentGateway,
                                    "mcc",
                                    e.target.value,
                                  );
                                }}
                                options={mccOptions ?? []}
                              />
                            );
                          }
                          return mccAllowed
                            ? findMccObject(gsi?.mcc)?.title || "-"
                            : "-";
                        })()}
                      </TableCell>
                      <TableCell className={styles.gsiTableCell}>
                        {renderBusinessPlan(
                          isEditing?.[merchantMdr?.cardType]?.[
                            gsi?.paymentGateway
                          ],
                          merchantMdr,
                          gsi,
                        )}
                      </TableCell>
                      <TableCell className={styles.gsiTableCell}>
                        {formatDate(gsi.updated)}
                      </TableCell>
                      <TableCell className={styles.gsiActionCell}>
                        <Stack
                          disabled={handleDisable(
                            merchantMdr?.cardType,
                            gsi?.paymentGateway,
                          )}
                          sx={{
                            color: "#006A8E",
                            cursor: handleDisable(
                              merchantMdr?.cardType,
                              gsi?.paymentGateway,
                            )
                              ? "not-allowed"
                              : "pointer",
                            pointerEvents: handleDisable(
                              merchantMdr?.cardType,
                              gsi?.paymentGateway,
                            )
                              ? "none"
                              : "auto",
                            opacity: handleDisable(
                              merchantMdr?.cardType,
                              gsi?.paymentGateway,
                            )
                              ? 0.5
                              : 1,
                            display: isEditing?.[merchantMdr?.cardType]?.[
                              gsi?.paymentGateway
                            ]
                              ? "none"
                              : "block",
                          }}
                          onClick={() => {
                            resetState();
                            setIsEditingState(
                              merchantMdr?.cardType,
                              gsi?.paymentGateway,
                              true,
                            );
                          }}
                        >
                          <div>{t(`${MERCHANT_PAGE_LOCALE_KEY}button.edit`)}</div>
                        </Stack>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"flex-start"}
                          gap={2}
                          flexWrap={"wrap"}
                        >
                          {renderSaveButtons(merchantMdr, gsi)}
                          <Button
                            sx={{
                              display: !isEditing?.[merchantMdr?.cardType]?.[
                                gsi?.paymentGateway
                              ]
                                ? "none"
                                : "block",
                              visibility: !isEditing?.[merchantMdr?.cardType]?.[
                                gsi?.paymentGateway
                              ]
                                ? "hidden"
                                : "",
                            }}
                            variant="outline-normal-button"
                            onClick={() => {
                              setIsEditingState(
                                merchantMdr?.cardType,
                                gsi?.paymentGateway,
                                false,
                              );
                              resetState();
                            }}
                            type="button"
                            disabled={isLoading}
                          >
                            {t(`${MERCHANT_PAGE_LOCALE_KEY}button.cancel`)}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      })}
      <AmexSponsoredMerchantDialog
        isOpen={isAmexBbmslFormDiaglogOpen}
        onClose={async (result) => {
          setIsAmexBbmslFormDiaglogOpen(false);
          if (result?.submitted) {
            // Optimistically keep enabled true and persist across incoming prop refreshes
            updateGsiState("AMEX", "AMEX_BBMSL", "enabled", true);
            setForceAmexEnabled(true);
            setAmexSponsoredMerchant({ exists: true });
            setHasFetchedAmexData(true);
            await fetchMerchantInfoAndGsi("update");
          } else {
            // Revert toggle if user cancelled
            updateGsiState("AMEX", "AMEX_BBMSL", "enabled", false);
            setForceAmexEnabled(false);
          }
        }}
        merchantId={merchantId}
        mccOptions={mccOptions}
        isLoading={isAmexFormLoading}
        amexSponsoredMerchant={amexSponsoredMerchant}
        merchant={merchant}
        onFormSubmit={async (formData) => {
          // Handle form submission with GSI operation
          const amexGsi = {
            ...getGsiFromState("AMEX", "AMEX_BBMSL"),
            mcc: formData.mccCode,
            businessPlan: "sme",
          };
          await handleGsiOperation(amexGsi);
        }}
        updateGsiState={updateGsiState}
      />
    </div>
  );
};

export default React.memo(PaymentGateways);
